import React, { useEffect, useState, Suspense, lazy } from "react";
import {
  BrowserRouter as Router,
  Switch,
  Route,
  useHistory,
} from "react-router-dom";
import ScrollToTop from "./helper/ScrollToTop";
import { GlobalStyle } from "./components/common/styles/global.js";
import { SnackbarProvider } from "notistack";
import notistack, { SnackbarUtilsConfigurator } from "./puglins/notistack";
import { auth, subjectGrade } from "./puglins/Utils/models";
import { useDispatch, useSelector } from "react-redux";
import {
  getAuthUser,
  getGradeFailure,
  getGradeSuccess,
  getLanguageFailure,
  getLanguageSuccess,
  getNotificationData,
  openSocket,
  setSocket,
  clearSocket,
  getSubjectFailure,
  getSubjectSuccess,
  isLogin,
} from "./actions/initialData";
import { getClassHistory } from "./actions/student.js";
import "./locales/i18n";
import { useDarkMode } from "@rbnd/react-dark-mode";
import noNetworkAction from "./puglins/Utils/errorManage.js";
import { useTranslation } from "react-i18next";
import Loader from "./components/common/Loader.js";
import { useDelayedLoader } from "./helper/useDelayedLoader.js";
import OfflineBannerNd from "./components/nouveauDesign/etats/OfflineBannerNd.js";
import { TOAST_ICONS } from "./components/nouveauDesign/etats/toasts.js";
import MainRoutes from "./routes/index.js";
import { useIdleTimer } from "react-idle-timer";
import { sendPushNotification } from "./puglins/pushNotification.js";
import manageClassStorage from "./helper/OnGoingClassManage.js";
import { createRequestDeduper, keepUserRoomJoined } from "./helper/userRoom.js";
import { announceRequest } from "./helper/requestSound";
import { playSound } from "./helper/sounds";
import { keepAccountInSync } from "./helper/accountSync.js";
import { markMissedRequest } from "./helper/teacherPresence.js";
import { useHiddenTabTimers } from "./helper/ongletCache.js";
import { OPEN_ASSISTANT_EVENT } from "./helper/assistant.js";
import TeacherOnlinePrompt from "./components/TeacherOnlinePrompt.js";
import ComputerHint from "./components/ComputerHint.js";
import UpcomingClassBanner from "./components/UpcomingClassBanner.js";
import NewDesign from "./components/nouveauDesign/NewDesign.js";
import { followSystemTheme, isAutoTheme, keepAutoInSync, noStoredTheme, systemTheme, useFirstVisitTheme } from "./helper/themeChoice.js";
import { loadFeatures, useFeature } from "./helper/features.js";
import { carnetFirst } from "./helper/carnet.js";
import { RELAY_FALLBACK_MS, hasPrerender, relayPrerender } from "./helper/prerendu.js";

const ChatBot = lazy(() => import("./components/common/ChatBot.js"));
const ResumeMeeting = lazy(() => import("./components/common/ResumeMeeting.js"));
const Alert = lazy(() => import("./components/Alert.js"));
// 📒 Carnet de suivi (tour 58) : fenêtre de la note du prof à la fin d'un cours
const CarnetNoteNd = lazy(() => import("./components/nouveauDesign/carnet/CarnetNoteNd.js"));
// ⚡ Réservations récentes (première apparition après 20 s) : chargées 10 s après le démarrage, hors
// du fichier principal et sans appel à l'API pendant le premier affichage (tour 38)
const SocialProof = lazy(() => import("./components/common/SocialProof"));
export const SOCIAL_PROOF_DELAY_MS = 10000;

function App() {
  const [mounted, setMounted] = useState(false);
  // 🌗 Première visite : lue au premier affichage, avant que le thème soit enregistré
  const [firstVisit] = useState(noStoredTheme);
  const dispatch = useDispatch();
  const { authUserData, socket } = useSelector(
    ({ intialDataReducer }) => intialDataReducer
  );

  const {
    t,
    i18n: { changeLanguage },
  } = useTranslation();
  const [openChat, setOpenChat] = useState(false);
  const [socialProofReady, setSocialProofReady] = useState(false);
  useEffect(() => {
    const id = setTimeout(() => setSocialProofReady(true), SOCIAL_PROOF_DELAY_MS);
    return () => clearTimeout(id);
  }, []);
  // 💬 « Besoin d'aide ? » ouvert depuis une page (« Mes demandes » sans abonnement Premium)
  useEffect(() => {
    const openAssistant = () => setOpenChat(true);
    window.addEventListener(OPEN_ASSISTANT_EVENT, openAssistant);
    return () => window.removeEventListener(OPEN_ASSISTANT_EVENT, openAssistant);
  }, []);
  const bottom = 20;
  // ⏳ Premier chargement : attente réelle du compte et des interrupteurs, et des niveaux, des
  // langues et des matières sauf pour un visiteur du nouveau design (tour 55, voir plus bas). L'écran
  // n'apparaît qu'au-delà de 300 ms, puis reste au moins 500 ms (helper/useDelayedLoader.js).
  const [loading, setLoading] = useState(true);
  const { loader, ready } = useDelayedLoader(loading);
  const [paymentModel, setPaymentModel] = useState({
    classId: 'null',
    model: false,
  });
  // 📒 Carnet de suivi (tour 58) : cours qui vient de se terminer, noté par le prof avant la fenêtre de
  // l'avis (lu par l'écouteur du socket au moment de l'événement, d'où la référence)
  const carnetOn = useFeature("carnet_suivi");
  const carnetRef = React.useRef(false);
  carnetRef.current = carnetOn === true;
  const [carnetAfter, setCarnetAfter] = useState(null);
  // ⏱️ Cours trop court pour un avis (review_allowed de l'API, tour 66, A13) : la fenêtre s'ouvre quand même et le dit,
  // pour l'élève comme pour le prof (tour 69, D1). Avant : pas de fenêtre du tout pour l'élève, rien de dit
  const openReview = (data) =>
    setPaymentModel((prev) => ({
      ...prev,
      classId: data,
      model: authUserData?.userType===1?(!data?.review?.teacher_review):(!data?.review?.student_review),
      modelNo: 4,
    }));

  // --- LES DEUX LECTEURS AUDIO ---
  // Demandes de cours déjà affichées (le serveur renvoie celles en attente à chaque reconnexion)
  const requestDeduperRef = React.useRef(null);
  if (!requestDeduperRef.current) requestDeduperRef.current = createRequestDeduper();
  const { mode, setMode } = useDarkMode();
  const history = useHistory();

  const [isTabHidden, setIsTabHidden] = useState(document.visibilityState === "hidden");

  // Surveille si tu changes d'onglet
  useEffect(() => {
    const handleVisibility = () => setIsTabHidden(document.visibilityState === "hidden");
    window.addEventListener("visibilitychange", handleVisibility);
    return () => window.removeEventListener("visibilitychange", handleVisibility);
  }, []);

  // --- SYNCHRONISATION DYNAMIQUE ET SAUVEGARDE DU THEME ---
  useEffect(() => {
    // 🌗 Clair ou sombre choisi alors qu'« Automatique » était gardé : « Automatique » est quitté
    if (mounted) keepAutoInSync(mode);
    if (mode === "dark") {
      document.body.classList.add("dark-mode", "rs-theme-dark");
      document.body.classList.remove("light-mode");
      localStorage.setItem("dark-mode", "dark");
    } else {
      document.body.classList.add("light-mode");
      document.body.classList.remove("dark-mode", "rs-theme-dark");
      localStorage.setItem("dark-mode", "light");
    }
  }, [mode, mounted]);

  // 🌗 « Automatique » : le site suit le navigateur quand il passe en clair ou en sombre
  const setModeRef = React.useRef(setMode);
  setModeRef.current = setMode;
  useEffect(() => followSystemTheme((theme) => setModeRef.current(theme)), []);

  // --- INITIALISATION AU DÉMARRAGE ---
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const token = localStorage.getItem("token");
      // ⚡ Niveaux, langues et matières (tour 55) : demandés tout de suite, mais un visiteur ne les attend
      // plus pour voir la page. Les pages publiques les remplissent à leur arrivée (menus inactifs et
      // place gardée en attendant). Un compte connecté (pages privées construites avec ces listes) les
      // attend. Les interrupteurs sont attendus.
      const lists = Promise.all([dispatch(getGrade()), dispatch(getLanguages()), dispatch(getSubjects())]);
      await Promise.all([token ? dispatch(getAuthUser(history)) : null, loadFeatures()]);
      if (token) await lists;
      manageDomainSpecificLang();
    };
    fetchData().finally(() => setLoading(false));

    const userChoice = localStorage.getItem("dark-mode");
    if (isAutoTheme()) {
      setMode(systemTheme());
    } else if (userChoice) {
      setMode(userChoice);
    } else {
      const sysTheme = window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
      setMode(sysTheme);
      localStorage.setItem("dark-mode", sysTheme);
    }

    setMounted(true);
  }, []);

  // 🌗 Première visite : le thème suit le navigateur (« Automatique »)
  useFirstVisitTheme({ firstVisit, setMode });

  // 🚀 Page préparée (tour 55) : la page du site prend le relais en apparaissant. Si elle n'est pas
  // apparue peu après la fin du chargement (adresse inattendue, erreur), le site s'affiche quand même
  useEffect(() => {
    if (!ready || !hasPrerender()) return undefined;
    const id = setTimeout(relayPrerender, RELAY_FALLBACK_MS);
    return () => clearTimeout(id);
  }, [ready]);

  // --- CONNEXION TEMPS RÉEL (SOCKET) ---
  // 🔌 Seulement pour un compte connecté : notifications, cours, présence. Un visiteur n'en ouvre
  // aucune (avant : ouverte au démarrage pour tout le monde), les statuts en ligne de l'accueil et
  // de la recherche viennent de l'API. Fermée à la déconnexion du compte, rouverte pour un autre.
  // (socket.io chargé à part depuis le tour 50 : ouverte dès que son code est arrivé, fermée tout de
  // suite si le compte a changé entre-temps)
  useEffect(() => {
    if (!authUserData?._id) return;
    let accountSocket = null;
    let closed = false;
    openSocket()
      .then((opened) => {
        if (closed) return opened.disconnect();
        accountSocket = opened;
        dispatch(setSocket(opened));
      })
      .catch(() => {});
    return () => {
      closed = true;
      if (accountSocket) accountSocket.disconnect();
      dispatch(clearSocket());
    };
  }, [authUserData?._id, dispatch]);

  // --- SALLE SOCKET DE L'UTILISATEUR ---
  // Rejointe à chaque connexion du socket, reconnexions automatiques comprises (helper/userRoom)
  useEffect(() => {
    if (!socket || !authUserData?._id) return;
    return keepUserRoomJoined(socket, () => auth.getToken());
  }, [socket, authUserData?._id]);

  // Compte relu à la fin d'un cours (terminé ou annulé) et après une reconnexion : sinon
  // le site garde l'élève « en cours » jusqu'au rechargement (helper/accountSync)
  useEffect(() => {
    if (!socket || !authUserData?._id) return;
    return keepAccountInSync(socket, () => dispatch(getAuthUser(history)));
  }, [socket, authUserData?._id, dispatch, history]);

  // 🟢 Prof passé hors ligne par le serveur (absence de plus de 4 minutes) : compte relu, la
  // fenêtre « Passer en ligne ? » s'affiche (TeacherOnlinePrompt)
  useEffect(() => {
    if (!socket || authUserData?.userType !== 1 || !authUserData?._id) return;
    const onPresence = (data) => String(data?.userId) === String(authUserData._id) && data?.online === 0 && dispatch(getAuthUser(history));
    socket.on("user-presence-change", onPresence);
    return () => socket.off("user-presence-change", onPresence);
  }, [socket, authUserData?._id, authUserData?.userType, dispatch, history]);

  // 🔔 Notifications : une fois par compte connecté (avant : à chaque relecture du
  // compte, l'effet des événements du socket ci-dessous dépendant du compte entier)
  useEffect(() => {
    if (!authUserData?._id) return;
    dispatch(getNotificationData(history));
    // Plus de coordonnées de l'ancien robot de contact (tour 51) : téléchargées à chaque page pour un
    // compte connecté, affichées nulle part
  }, [authUserData?._id, dispatch, history]);

  // --- SOCKET IO EVENTS ---
  useEffect(() => {
    localStorage.getItem("token") && dispatch(isLogin(true));
    // Visiteur : pas de connexion temps réel, rien à écouter
    if (!socket) return;

    socket?.on("class-request", (data) => {
      // Déjà affichée (renvoyée par le serveur après une reconnexion) : rien de plus
      if (!requestDeduperRef.current(data?.class_information?._id)) return;
      // 🔔 Une seule sonnerie par demande, socket ou tableau de bord (helper/requestSound.js)
      announceRequest(data?.class_information?._id);
      notistack.toast(data?.message);
      sendPushNotification(data?.message);
      dispatch(getClassHistory(history, authUserData?._id));
      dispatch(getNotificationData());
      setPaymentModel((prev) => ({
        ...prev,
        classId: data?.class_information,
        model: true,
        modelNo: 5,
      }));
      sessionStorage.setItem("RequestId", data?.class_information?._id);
    });

    socket?.on("invitation-accepted", (data) => {
      notistack.toast(data?.message);
      sendPushNotification(data?.message);
      setPaymentModel((prev) => ({
        ...prev,
        classId: data,
        model: true,
        modelNo: 1,
      }));
      dispatch(getClassHistory(history, authUserData?._id));
      dispatch(getNotificationData());
    });

    socket?.on("invitation-rejected", (data) => {
      notistack.error(data?.message);
      sendPushNotification(data?.message);
      setPaymentModel((prev) => ({
        ...prev,
        classId: data?.class_id,
        model: true,
        modelNo: 3,
        advertData: data?.adverts,
        // Le prof n'a pas répondu à temps (tour 62, B2) : la fenêtre le dit, avec son prénom
        missed: data?.missed === true,
        teacherName: data?.teacher,
      }));
      dispatch(getClassHistory(history, authUserData?._id));
      dispatch(getAuthUser(history));
      dispatch(getNotificationData());
    });

    const onClassCompleted = (data) => {
      // 📒 Prof, carnet allumé : la note du cours d'abord, la fenêtre de l'avis à sa fermeture
      if (carnetFirst({ userType: authUserData?.userType, enabled: carnetRef.current, data })) setCarnetAfter(data);
      else openReview(data);
      notistack.toast(t("notiClassCompleted"));
      dispatch(getNotificationData());
      manageClassStorage.deleteDetails();
    };
    socket?.on("class-completed", onClassCompleted);

    socket?.on("class-started", (data) => {
      if (data?.from_waiting_room) {
        // Cours venant de la salle d'attente → popup "C'est à vous !"
        playSound("success");
        setPaymentModel((prev) => ({
          ...prev,
          classId: data,
          model: true,
          modelNo: 12,
        }));
      } else {
        // Cours normal → juste toast discret
        window.location.pathname !== "/ZoomClasses" && notistack.toast(t("notiTutorStarted"));
      }
      dispatch(getClassHistory(history, authUserData?._id));
      dispatch(getNotificationData());
    });

    authUserData?.userType === 2 && socket?.on("favourite-teacher", (data) => {
      notistack.toast(data?.message);
      dispatch(getNotificationData());
    });

    socket?.on("level-upgrade", (data) => {
      notistack.toast(data?.message);
      dispatch(getNotificationData());
    });

    socket?.on("exit-waiting-room", (data) => {
      notistack.toast(data?.message);
      dispatch(getNotificationData());
    }); 

    socket?.on("wallet-recharge-admin", (data) => {
      notistack.toast(data?.message);
      dispatch(getAuthUser(history));
    });

    // Cours annulé par le prof (problème technique) ou non démarré dans les 10 min après son
    // acceptation : élève remboursé, plus de « Retour au cours » ; le prof est aussi prévenu
    const onClassCancelled = (data) => {
      notistack.error(data?.message || "Votre cours a été annulé (problème technique) et remboursé sur votre portefeuille.");
      sendPushNotification(data?.message);
      // « Retour au cours » retiré seulement si c'est ce cours (le prof peut être dans un autre)
      if (!data?.classId || manageClassStorage.getDetails()?.id === data.classId) manageClassStorage.deleteDetails();
      dispatch(getClassHistory(history, authUserData?._id));
      // Compte (plus « en cours ») relu par keepAccountInSync
      dispatch(getNotificationData());
    };
    socket?.on("class-cancelled", onClassCancelled);

    socket?.on("waiting-room-start-soon", (data) => {
      sendPushNotification(data?.message);
      notistack.toast("Le professeur est disponible, préparez-vous\u00a0!");
      playSound("success");
      dispatch(getClassHistory(history, authUserData?._id));
      dispatch(getNotificationData());
    });

    socket?.on("student-waiting-after-class", (data) => {
      playSound("success");
      setPaymentModel((prev) => ({
        ...prev,
        classId: data,
        model: true,
        modelNo: 10,
      }));
    });

    socket?.on("invitation-auto-rejected", (data) => {
      // Demande refusée par la tâche du serveur, le prof hors ligne : la fenêtre « Passer en ligne ? » dira pourquoi
      // (tour 62, B4)
      markMissedRequest();
      sessionStorage?.getItem("RequestId") === data?.data && setPaymentModel((prev) => ({
        ...prev,
        classId: null,
        model: false,
        modelNo: 0,
      }));
      dispatch(getClassHistory(history, authUserData?._id));
      dispatch(getAuthUser(history));
    });

    const handleOnline = () => { socket.connect(); };
    const handleOffline = () => {
      socket.emit('user-disconnect',authUserData?._id);
      socket.disconnect(); 
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      socket?.off("class-request");
      socket?.off("invitation-accepted");
      socket?.off("invitation-rejected");
      // Seulement ces écouteurs : keepAccountInSync écoute aussi « class-completed » et
      // « class-cancelled », et cet effet est relancé à chaque mise à jour du profil
      socket?.off("class-completed", onClassCompleted);
      socket?.off("class-started");
      socket?.off("favourite-teacher");
      socket?.off("joinRoom");
      socket?.off("waiting-room-start-soon");
      socket?.off("exit-waiting-room");
      socket?.off("invitation-auto-rejected");
      socket?.off("wallet-recharge-admin");
      // La salle de cours (ZoomMeeting) écoute aussi « class-cancelled »
      socket?.off("class-cancelled", onClassCancelled);
      socket?.off("student-waiting-after-class");
      socket?.off("user-disconnect");
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [socket, authUserData]);

  // ========================================================
  // 🫀 HEARTBEAT — signal de vie fiable pour le PresenceReaper serveur
  // ========================================================
  // Émet toutes les 30s. Le back-end rafraîchit User.last_seen à chaque
  // tick ; le cron côté serveur (PresenceReaper) passe offline les users
  // dont last_seen est trop vieux (>4 min).
  //
  // Desktop : le heartbeat tourne TOUJOURS, même onglet caché. Les profs
  //           laissent volontairement l'onglet en arrière-plan en attendant
  //           des demandes de cours.
  // Mobile  : silence radio quand onglet hidden (clearInterval), reprise
  //           + émission immédiate au retour visible.
  useEffect(() => {
    if (!authUserData?._id || !socket) return;

    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry/i.test(navigator.userAgent);

    let heartbeatIntervalId = null;

    const emitHeartbeat = () => {
      if (!socket.connected) return;
      // Sur mobile, garde-fou supplémentaire si la fenêtre est cachée.
      if (isMobile && document.hidden) return;
      socket.emit("heartbeat", { userId: authUserData._id });
    };

    const startHeartbeat = () => {
      if (heartbeatIntervalId) return;
      emitHeartbeat();
      heartbeatIntervalId = setInterval(emitHeartbeat, 30000);
    };

    const stopHeartbeat = () => {
      if (heartbeatIntervalId) {
        clearInterval(heartbeatIntervalId);
        heartbeatIntervalId = null;
      }
    };

    const handleHeartbeatVisibility = () => {
      // Sur desktop, on ignore visibilitychange — le heartbeat continue.
      if (!isMobile) return;
      if (document.hidden) {
        stopHeartbeat();
      } else {
        startHeartbeat();
      }
    };

    // Démarrage initial
    if (!isMobile || !document.hidden) {
      startHeartbeat();
    }

    document.addEventListener("visibilitychange", handleHeartbeatVisibility);

    return () => {
      stopHeartbeat();
      document.removeEventListener("visibilitychange", handleHeartbeatVisibility);
    };
  }, [authUserData?._id, socket]);

  // ========================================================
  // 🕒 GESTION DE L'INACTIVITÉ ET DE LA PRÉSENCE (BLOC UNIQUE)
  // ========================================================

  const onIdleFunction = () => {
    if (!isTabHidden && authUserData?.userType === 1 && authUserData?.online === 1) {
      playSound("alert");
      setPaymentModel({ modelNo: 8, model: true });
    }
  };

  const idleTimer = useIdleTimer({
    timeout: 5400000, // Prod: 1h30 (5400000 ms)
    onIdle: onIdleFunction,
    debounce: 500,
    disabled: isTabHidden,
  });

  // « Toujours là ? » sans réponse : hors ligne au bout de 30 s (Alert.js, fenêtre 8). Le second décompte de
  // 5 minutes qui était ici n'était jamais atteint, retiré (tour 65, C2)

  useEffect(() => {
    if (!authUserData?._id) return;

    // Onglet caché : décomptes de useHiddenTabTimers, plus bas (tour 62, B1)
    const handleVisibilityChange = () => {
      if (document.visibilityState !== "hidden") {
        // --- RETOUR SUR LE SITE ---
        idleTimer.reset(); 
        if (socket && !socket.connected) socket.connect();
        dispatch(getAuthUser(history));
      }
    };

    // Capture la fermeture totale de l'onglet ou le rafraîchissement (F5)
    const handleTabClose = (event) => {
      if (document.visibilityState !== "hidden") {
        handleUnload("closing");
      }
    };

    window.addEventListener("beforeunload", handleTabClose);
    window.addEventListener("pagehide", handleTabClose);
    window.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("beforeunload", handleTabClose);
      window.removeEventListener("pagehide", handleTabClose);
      window.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [authUserData, socket, paymentModel, idleTimer]);

  // 🕒 Onglet caché (tour 62, B1, helper/ongletCache.js) : à l'ordinateur, la notification « Toujours là ? » et le son
  // au bout d'1 h 30, hors ligne 5 minutes après ; au téléphone, 4 et 5 minutes, même notification (tour 63, A2). Hors
  // de l'effet ci-dessus, relancé à chaque rendu, qui arrêtait les décomptes
  const isMobileDevice = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  // Seulement pour un prof en ligne (tour 63, A5) : jamais pour un élève, ni pour un prof hors ligne ou en cours
  useHiddenTabTimers({
    enabled: authUserData?.userType === 1 && authUserData?.online === 1,
    isMobile: isMobileDevice,
    onWarn: () => {
      sendPushNotification(t("teacherOnline.stillThereNotice"));
      playSound("alert");
    },
    onOffline: () => handleUnload("closing"),
  });
  // ========================================================

  const handleUnload = (triggerType) => {
    if (!authUserData?._id) return;

    const url = process.env.REACT_APP_URL_API + `/update-online-status/${authUserData?._id}/${authUserData?.online===2?2:0}`;
    const dataStr = JSON.stringify({ data: authUserData?._id });

    if (triggerType === "idle") {
      fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: dataStr,
        keepalive: true,
      })
        .then(() => dispatch(getAuthUser(history)))
        .catch((error) => console.error("Error sending data:", error));
    } else {
      const blob = new Blob([dataStr], { type: "application/json" });
      navigator.sendBeacon(url, blob);
    }

    sessionStorage.setItem(
      "online",
      JSON.stringify({ status: authUserData?.online, user_id: authUserData?._id })
    );      

    if (socket) socket.disconnect();
  };

  const getLanguages = () => {
    return (dispatch) => {
      return subjectGrade.getLangauge()
        .then((response) => dispatch(getLanguageSuccess(response?.data)))
        .catch((error) => {
          error?.message === "Network Error" && noNetworkAction(history);
          dispatch(getLanguageFailure(error));
        });
    };
  };

  const getGrade = () => {
    return (dispatch) => {
      return subjectGrade.getGrade()
        .then((res) => dispatch(getGradeSuccess(res?.data)))
        .catch((err) => {
          err?.message === "Network Error" && noNetworkAction(history);
          dispatch(getGradeFailure(err));
        });
    };
  };

  const getSubjects = () => {
    return (dispatch) => {
      return subjectGrade.getSubjects()
        .then((res) => dispatch(getSubjectSuccess(res?.data)))
        .catch((err) => {
          err?.message === "Network Error" && noNetworkAction(history);
          dispatch(getSubjectFailure(err));
        });
    };
  };

  const manageDomainSpecificLang = () => {
    if (window.location.origin.includes(".fr")) {
      changeLanguage("fr");
    }
  };

  // Avant l'écran de chargement (300 premières ms) : le fond seul
  if (!mounted || (!loader && !ready)) {
    return <div style={{ minHeight: "100vh", backgroundColor: mode === "dark" ? "#1e1e1e" : "#ffffff" }} />;
  }

  return (
    <>
      {loader ? (
        <Loader immediate />
      ) : (
        <Router>
          {/* 🎨 Notifications d'après Etats.html (icône, bord de couleur), erreurs gardées jusqu'à leur
              fermeture */}
          <SnackbarProvider maxSnack={5} style={{ fontSize: "14px" }} iconVariant={TOAST_ICONS}>
            <SnackbarUtilsConfigurator persistErrors />
            <GlobalStyle />
            {/* 🎨 Styles communs du site (jetons, boutons, classe « nd » sur <body>) */}
            <NewDesign />
            {/* 📶 Bandeau « Connexion Internet perdue » tant qu'Internet est coupé */}
            <OfflineBannerNd />
            <ScrollToTop />
            {/* 👇 LA BALISE MAIN EST ICI 👇 */}
            <main>
              <Switch>
                <Route>
                  <MainRoutes />
                </Route>
              </Switch>
            </main>
            {/* 👆 FIN DE LA BALISE MAIN 👆 */}
            {carnetAfter && (
              <Suspense fallback={null}>
                <CarnetNoteNd
                  key={String(carnetAfter.class_id)}
                  classId={String(carnetAfter.class_id)}
                  auto
                  onDone={() => {
                    const data = carnetAfter;
                    setCarnetAfter(null);
                    openReview(data);
                  }}
                />
              </Suspense>
            )}
            {paymentModel?.model && (
              <Suspense>
                <Alert
                  paymentModel={paymentModel}
                  setPaymentModel={setPaymentModel}
                  handleUnload={handleUnload}
                />
              </Suspense>
            )}
            {authUserData && (
              <>
                {window.location.pathname !== "/ZoomClasses" &&
                  manageClassStorage.getDetails() && (
                    <Suspense fallback={<div>...</div>}>
                      <ResumeMeeting bottom={bottom} />
                    </Suspense>
                  )}
                {authUserData?.userType === 1 && (
                  <TeacherOnlinePrompt authUserData={authUserData} onChanged={() => dispatch(getAuthUser(history))} />
                )}
                {/* 💻 Au téléphone, conseil de l'ordinateur avant chaque passage en ligne (tour 49) */}
                {authUserData?.userType === 1 && <ComputerHint authUserData={authUserData} />}
                {/* 📅 Prochain cours réservé (planning) : salle ouverte, attente, retard, élève absent */}
                <UpcomingClassBanner authUserData={authUserData} socket={socket} />
              </>
            )}
            {/* 💬 « Besoin d'aide ? » pour tous, visiteurs sans compte compris (limite par adresse IP,
                formulaire de contact à la place d'une transmission à l'équipe) */}
            <Suspense fallback={<div>...</div>}>
              <ChatBot
                bottom={bottom}
                openChat={openChat}
                setOpenChat={setOpenChat}
                visitor={!authUserData}
                premium={authUserData?.membership === "Premium"}
              />
            </Suspense>
            {socialProofReady && window.location.pathname !== "/ZoomClasses" && (
              <Suspense fallback={null}>
                <SocialProof />
              </Suspense>
            )}
          </SnackbarProvider>
        </Router>
      )}
    </>
  );
}

export default App;